import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";

import PageSideBar from "components/PageSideBar";
import TextField from "components/TextField";
import Button from "components/Button";
import Tabs from "components/Tabs";
import FileUploadArea from "./components/FileUploadArea";
import ProjectService from "api/Project/ProjectService";
import { FilePurpose } from "api/Storage/StorageService";
import { AppRoutes } from "pages/constants/routes";
import { uploadFile } from "utils/storage";
import ComboBoxField from "components/ComboBoxField";
import ComboBoxItem from "components/ComboBox/ComboBoxItem";
import { TimeIntervalData } from "components/TimeIntervalPicker/TimeIntervalPicker";
import HelpHoverIcon from "components/HelpHoverIcon";
import { mergeIntervals } from "utils/interval";
import CURRENT_DEPLOYMENT from "./CurrentDeployment";
import CURRENT_REVENUE from "./CurrentRevenue";
import PRODUCT_INFO from "./ProductInfo";
import DATA_TYPE from "./DataType";
import {
  newProjectSchema,
  newProjectSchemaBlankError,
  NewProjectSchemaError,
} from "validation/schemas/new_project";

import styles from "./NewProject.module.scss";
import { Check } from "lucide-react";
import NonUseTimingsDialog from "components/NontUseTimingsDialog";

const NewProject = () => {
  const [projectName, setProjectName] = useState<string>("");

  // Project Information
  const [nonUseTimings, setNonUseTimings] = useState<TimeIntervalData[]>([]);
  const [currentCost, setCurrentCost] = useState<string>("");
  const [useCase, setUseCase] = useState<string>("");
  const [currentRevenue, setCurrentRevenue] = useState<ComboBoxItem<string>>();
  const [currentDeployment, setCurrentDeployment] =
    useState<ComboBoxItem<string>>();
  const [currentDeploymentOthers, setCurrentDeploymentOthers] =
    useState<string>("");
  const [productInfo, setProductInfo] = useState<ComboBoxItem<string>[]>([]);

  // Model Upload
  const [modelTypeIndex, setModelTypeIndex] = useState<number>(0);
  const [modelFiles, setModelFiles] = useState<File[]>([]);

  // Data Upload
  const [batchSize, setBatchSize] = useState<string>("");
  const [inputShape, setInputShape] = useState<string>("");
  const [outputShape, setOutputShape] = useState<string>("");
  const [inputDType, setInputDType] = useState<ComboBoxItem<string>>();
  const [outputDType, setOutputDType] = useState<ComboBoxItem<string>>();
  const [sampleDataFiles, setSampleDataFiles] = useState<File[]>([]);

  const [openNonUseTimingsModel, setOpenNonUseTimingsModel] =
    useState<boolean>(false);

  const [errors, setErrors] = useState<NewProjectSchemaError>(
    newProjectSchemaBlankError,
  );

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [projectCreated, setProjectCreated] = useState<boolean>(false);
  const [modelUploaded, setModelUploaded] = useState<boolean>(false);
  const [sampleDataUploaded, setSampleDataUploaded] = useState<boolean>(false);
  const [createdProjectId, setProjectCreatedId] = useState<string>();

  const navigate = useNavigate();

  const fileComparator = (f1: File, f2: File): number => {
    const getPrefix = (f: File) =>
      parseInt((f.name.match(/^\d+/) || "99999")[0], 10);
    return getPrefix(f1) - getPrefix(f2);
  };

  const resetLoadingStates = () => {
    setIsSubmitting(false);
    setProjectCreated(false);
    setModelUploaded(false);
    setSampleDataUploaded(false);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const result = newProjectSchema.safeParse({
        projectName,
        currentCost,
        useCase,
        currentRevenue,
        currentDeployment,
        currentDeploymentOthers,
        productInfo,
        modelType: modelTypeIndex == 0 ? "pytorch" : "onnx",
        modelFiles,
        batchSize,
        inputShape,
        outputShape,
        inputDType,
        outputDType,
        sampleDataFiles,
      });

      if (!result.success) {
        const newErrors = { ...newProjectSchemaBlankError };

        result.error?.errors.forEach((error) => {
          const field = error.path[0] as keyof NewProjectSchemaError;
          newErrors[field] = error.message;
        });
        setErrors(newErrors);
        resetLoadingStates();
        return;
      }
      setErrors(newProjectSchemaBlankError);

      const response = await ProjectService.newProject(
        projectName,
        currentCost,
        useCase,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        currentRevenue!.value,
        currentDeployment?.value || currentDeploymentOthers,
        productInfo.map((option) => option.value),
        mergeIntervals(nonUseTimings.map((data) => [data.start, data.end])),
        modelTypeIndex == 0 ? "pytorch" : "onnx", // TODO: Improve value retrieval
        batchSize,
        inputShape,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        inputDType!.value,
        outputShape,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        outputDType!.value,
      );
      if (response.project_id === undefined) {
        resetLoadingStates();
        return;
      }
      setProjectCreated(true);

      const project_id = response.project_id;
      setProjectCreatedId(project_id);
      for (const file of modelFiles) {
        await uploadFile(file, project_id, FilePurpose.Model);
      }
      setModelUploaded(true);

      for (const file of sampleDataFiles) {
        await uploadFile(file, project_id, FilePurpose.SampleData);
      }
      setSampleDataUploaded(true);

      const createCompeleteResponse =
        await ProjectService.projectCreationCompleted(project_id);
      if (!createCompeleteResponse.success) {
        throw new Error("Failed to send project creation confirmation");
      }

      navigate(AppRoutes.project.replace(":proj_id", project_id));
    } catch (err) {
      // handle error
      console.log("Error creating projects...");
      console.log(err);

      if (createdProjectId) ProjectService.deleteProject(createdProjectId);
    } finally {
      resetLoadingStates();
    }
  };

  const onFormChange =
    <T,>(onChange: (newValue: T) => void) =>
    (newValue: T) => {
      setErrors({ ...newProjectSchemaBlankError });
      onChange(newValue);
    };

  return (
    <div className={styles.page}>
      <PageSideBar />
      <div className={styles.content}>
        <div className={styles.title}>Create A New Project</div>
        <div className={styles.form}>
          <div className={styles.leftContainer}>
            <div className={styles.section}>
              <div className={styles.header}>
                <span className={styles.headerTitle}>Basic Information</span>
              </div>
              <div className={styles.sectionContent}>
                <TextField
                  value={projectName}
                  setValue={onFormChange(setProjectName)}
                  label="Project Name"
                  error={errors.projectName}
                />
              </div>
            </div>
            <div
              className={classNames(styles.section, styles.deploymentSection)}
            >
              <div className={styles.header}>
                <span className={styles.headerTitle}>Project Information</span>
                <a
                  href={`${process.env.REACT_APP_DOCS_URL}/project-setup/project-information`}
                  target="_blank"
                  className={styles.help}
                  rel="noreferrer"
                >
                  Need Help?
                </a>
              </div>
              <div className={styles.sectionContent}>
                <TextField
                  value={currentCost}
                  setValue={onFormChange(setCurrentCost)}
                  label="Current Cost / month"
                  helpMessage="Please describe your current cost plan (e.g. cost per request, cost per hour)"
                  error={errors.currentCost}
                />
                <TextField
                  value={useCase}
                  setValue={onFormChange(setUseCase)}
                  label="Use Case"
                  helpMessage="Please describe the purpose of the project and the type of model you are using. (e.g. chatbot)"
                  error={errors.useCase}
                />
                <div className={styles.twoColumns}>
                  <ComboBoxField
                    className={
                      currentDeployment?.value !== "Others"
                        ? styles.spanTwo
                        : ""
                    }
                    selected={currentDeployment}
                    label="Current Deployment Strategy"
                    options={CURRENT_DEPLOYMENT}
                    onChange={onFormChange(setCurrentDeployment)}
                    error={errors.currentDeployment}
                  />
                  {currentDeployment?.value === "Others" && (
                    <TextField
                      value={currentDeploymentOthers}
                      setValue={onFormChange(setCurrentDeploymentOthers)}
                      label="Others"
                      error={errors.currentDeploymentOthers}
                    />
                  )}
                </div>
                <ComboBoxField
                  selected={currentRevenue}
                  label="Current Revenue from this model"
                  options={CURRENT_REVENUE}
                  onChange={onFormChange(setCurrentRevenue)}
                  error={errors.currentRevenue}
                />
                <ComboBoxField
                  multiple
                  selected={productInfo}
                  label="Select all that apply"
                  options={PRODUCT_INFO}
                  onChange={onFormChange(setProductInfo)}
                  error={errors.productInfo}
                />
                <div className={styles.buttonField}>
                  <div className={styles.fieldHeader}>
                    <div>Non-Use Timings</div>
                    <HelpHoverIcon message="Please select the period when no requests should be expected" />
                  </div>
                  <div
                    className={styles.button}
                    onClick={() => setOpenNonUseTimingsModel(true)}
                  >
                    Select Timings
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.rightContainer}>
            <div
              className={classNames(styles.section, styles.modelUploadSection)}
            >
              <div className={styles.header}>
                <span className={styles.headerTitle}>Upload Your Model</span>
                <a
                  href={`${process.env.REACT_APP_DOCS_URL}/project-setup/model-upload`}
                  target="_blank"
                  className={styles.help}
                  rel="noreferrer"
                >
                  Need Help?
                </a>
              </div>
              <div className={styles.sectionContent}>
                <div className={styles.modelType}>
                  <span>Model Type</span>
                  <Tabs
                    tabs={["Pytorch", "ONNX"]}
                    tabIndex={modelTypeIndex}
                    setTabIndex={setModelTypeIndex}
                  />
                </div>
                <div className={styles.instructions}>
                  <b>Instructions</b>
                  {modelTypeIndex === 0 && (
                    <ol>
                      <li>Export your pytorch model in TorchScript format</li>
                      <li>Upload the .pt file only</li>
                    </ol>
                  )}
                  {modelTypeIndex === 1 && (
                    <ol>
                      <li>Export your model in a .onnx format</li>
                      <li>Upload the .onnx file only</li>
                    </ol>
                  )}
                </div>
                <FileUploadArea files={modelFiles} setFiles={setModelFiles} />
                {errors.modelFiles.length > 0 && (
                  <div className={styles.error}>{errors.modelFiles}</div>
                )}

                <div className={styles.separator} />

                <TextField
                  value={batchSize}
                  setValue={onFormChange(setBatchSize)}
                  label="Input Batch Size"
                  helpMessage="A positive number indicate a fixed value, a negative number indicate a variable size with a maximum value."
                  error={errors.batchSize}
                />
                <div className={styles.twoColumns}>
                  <TextField
                    value={inputShape}
                    setValue={onFormChange(setInputShape)}
                    label="Input Shape"
                    helpMessage="Format: x,y,...: replace each letter with a number. A positive number indicate a fixed value, a negative number indicate a variable size with a maximum value."
                    error={errors.inputShape}
                  />
                  <TextField
                    value={outputShape}
                    setValue={onFormChange(setOutputShape)}
                    label="Output Shape"
                    helpMessage="Format: x,y,...: replace each letter with a number. A positive number indicate a fixed value, a negative number indicate a variable size with a maximum value."
                    error={errors.outputShape}
                  />
                </div>
                <div className={styles.twoColumns}>
                  <ComboBoxField
                    label="Input Type"
                    selected={inputDType}
                    options={DATA_TYPE}
                    onChange={onFormChange(setInputDType)}
                    error={errors.inputDType}
                  />
                  <ComboBoxField
                    label="Output Type"
                    selected={outputDType}
                    options={DATA_TYPE}
                    onChange={onFormChange(setOutputDType)}
                    error={errors.outputDType}
                  />
                </div>
              </div>
            </div>
            <div
              className={classNames(styles.section, styles.dataUploadSection)}
            >
              <div className={styles.header}>
                <span className={styles.headerTitle}>Sample Data</span>
                <a
                  href={`${process.env.REACT_APP_DOCS_URL}/project-setup/sample-data-upload `}
                  target="_blank"
                  className={styles.help}
                  rel="noreferrer"
                >
                  Need Help?
                </a>
              </div>
              <div className={styles.sectionContent}>
                <div className={styles.instructions}>
                  <b>Instructions</b>
                  <ol>
                    <li>
                      Upload at least <b>50</b> data points (input & output) to
                      enable verification of your uploaded model
                    </li>
                    <li>
                      The first uploaded data should be named as 0_in.npy and
                      0_out.npy
                    </li>
                    <li>
                      Subsequent files should be named similarly (<i>NUM</i>
                      _input.npy and <i>NUM</i>_output.npy)
                    </li>
                  </ol>
                </div>
                <FileUploadArea
                  files={sampleDataFiles}
                  setFiles={(newFiles) => {
                    newFiles.sort(fileComparator);
                    setSampleDataFiles(newFiles);
                  }}
                />
                {errors.sampleDataFiles.length > 0 && (
                  <div className={styles.error}>{errors.sampleDataFiles}</div>
                )}
              </div>
              <div className={styles.footer}>
                <div className={styles.buttonContainer}>
                  <Button
                    disabled={isSubmitting}
                    text="Create Project"
                    onClick={onSubmit}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Blocking container to show project creation progress */}
        {isSubmitting && (
          <div className={styles.submittingBlock}>
            <div className={styles.blockContainer}>
              <div className={styles.blockHeader}>
                Project Creation In Progress
              </div>
              {!projectCreated ? (
                <div className={styles.loading}>Creating Project . . .</div>
              ) : (
                <div className={styles.loaded}>
                  <Check /> <span>Project Created</span>
                </div>
              )}
              {!projectCreated ? (
                <></>
              ) : !modelUploaded ? (
                <div className={styles.loading}>Uploading your model . . .</div>
              ) : (
                <div className={styles.loaded}>
                  <Check /> <span>Model Uploaded</span>
                </div>
              )}
              {!projectCreated || !modelUploaded ? (
                <></>
              ) : !sampleDataUploaded ? (
                <div className={styles.loading}>
                  Uploading your sample data . . .
                </div>
              ) : (
                <div className={styles.loaded}>
                  <Check /> <span>Sample Data Uploaded</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <NonUseTimingsDialog
        open={openNonUseTimingsModel}
        setOpen={setOpenNonUseTimingsModel}
        nonUseTimings={nonUseTimings}
        setNonUseTimings={setNonUseTimings}
      />
    </div>
  );
};

export default NewProject;
